import type { AxleDiagramKind } from '../../../lib/vehicleAxleDiagram'

export const equipmentProfileLabels: Record<AxleDiagramKind, string> = {
  semi: 'Semi-remorque', trailer: 'Remorque', rigid: 'Porteur rigide', tractor: 'Tracteur routier', van: 'Fourgonnette', custom: 'Équipement personnalisé',
}

/** Angular cab and chassis inspired by a technical side elevation. */
function TractorCab({ x, width, frontArch }: { x: number; width: number; frontArch: number }) {
  return <svg x={x} y="0" width={width} height="145" viewBox="0 0 200 145" preserveAspectRatio="none">
    <path d={`M6 119V57L21 35Q27 26 43 23L173 7H187V118H${frontArch + 32}V131A32 32 0 0 0 ${frontArch - 32} 131H6Z`} className="fill-slate-50 dark:fill-slate-900" />
    <path d="M29 29L198 0L196 21L166 15M174 15V116H55V26" strokeLinejoin="round" />
    <path d="M15 58L30 36Q39 29 59 27V60H15Z" className="fill-sky-50 dark:fill-slate-800" />
    <path d="M70 27Q107 22 157 27V60H70Z" className="fill-sky-50 dark:fill-slate-800" />
    <path d="M65 26V116M165 23V114M149 70h10M10 68H48L43 77H9M9 81H41L36 90H9M9 94H34L29 104H9" />
    <path d={`M${frontArch - 39} 132Q${frontArch - 39} 105 ${frontArch - 19} 101Q${frontArch} 94 ${frontArch + 20} 102Q${frontArch + 39} 108 ${frontArch + 39} 132`} className="fill-slate-100 dark:fill-slate-800" />
    <path d="M3 116H28V132H1V119Z" className="fill-slate-200 dark:fill-slate-700" />
    <rect x="5" y="120" width="10" height="6" rx="1" />
    <path d="M0 43H23L20 54H0ZM21 47h14" className="fill-slate-100 dark:fill-slate-800" />
  </svg>
}

/** Decorative profile: the coordinate origin and the axle positions stay in AxleDiagram. */
export function EquipmentSideProfile({ kind, cabEnd, kingPin, fifthWheel, frontWheel, rearWheels = [], compact = false, y, height = 156, cropTop = 0 }: {
  kind: AxleDiagramKind; cabEnd: number; kingPin: number | null; fifthWheel: number | null
  frontWheel?: number; rearWheels?: number[]; compact?: boolean; y?: number; height?: number; cropTop?: number
}) {
  const left = 80, right = 920
  const cab = kind === 'tractor' ? Math.max(360, Math.min(480, cabEnd + 100)) : Math.max(230, Math.min(420, cabEnd))
  const cargo = (x: number) => <g data-profile-cargo>
    <rect x={x} y="12" width={right - x} height="100" rx="2" className="fill-slate-50 dark:fill-slate-900" />
    <path d={`M${x + 5} 15H915V109H${x + 5}Z M${x} 105H920 M910 13V111`} fill="none" opacity=".7" />
    {[32, 65, 94].map(y => <path key={y} d={`M909 ${y}h10m-5 -4v8`} />)}
    <path d={`M${x} 112H920V120H${x}Z`} className="fill-slate-200 dark:fill-slate-700" />
    <path d="M919 120V140H906M918 125H928" />
    <rect x={Math.max(x + 20, 490)} y="123" width="72" height="13" rx="2" fill="none" />
  </g>
  const frontArch = Math.max(75, Math.min(193, ((frontWheel ?? (left + cab) / 2) - left) / (cab - left) * 200))
  return <svg x="0" y={y ?? (compact ? 20 : 114)} width="100%" height={height} viewBox={`0 ${cropTop} 1000 ${156 - cropTop}`} preserveAspectRatio="none" aria-label={`Silhouette technique : ${equipmentProfileLabels[kind]}`} role="img" data-equipment-profile={kind}
    className="pointer-events-none text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" strokeWidth="1.2">
    <g vectorEffect="non-scaling-stroke">
      {kind === 'semi' && <>
        {cargo(left)}
        {kingPin != null && <g data-profile-kingpin><path d={`M${kingPin} 112V130m-6 0h12`} stroke="#0284c7" strokeWidth="2" /></g>}
        <path d="M300 120V146H288M307 120V146H320M285 147H323" />
        <path d="M80 4H920M84 7V13M916 7V13" />
      </>}
      {kind === 'trailer' && <>
        {cargo(left)}
        <path data-profile-drawbar d="M80 116L15 132H4V138H18L80 123M42 126L80 120" />
      </>}
      {kind === 'rigid' && <>{cargo(cab + 16)}<g data-profile-cab data-profile-cab-technical><TractorCab x={left} width={cab - left} frontArch={frontArch} /></g></>}
      {kind === 'tractor' && <>
        <g data-profile-cab data-profile-cab-technical data-profile-tractor-detailed>
          <TractorCab x={left} width={cab - left} frontArch={frontArch} />
          <path d={`M${cab + 4} 111H920V121H${cab + 4}Z`} className="fill-slate-200 dark:fill-slate-700" />
          <path d={`M${cab + 20} 124H${Math.max(cab + 90, (rearWheels[0] ?? 730) - 55)}V143H${cab + 20}Z`} className="fill-slate-100 dark:fill-slate-800" />
          <path d={`M${cab + 90} 125V142M${cab + 160} 125V142`} /><rect x={cab + 8} y="125" width="45" height="16" rx="1" className="fill-slate-100 dark:fill-slate-800" />
          {rearWheels.map((x, index) => <path key={index} d={`M${x - 43} 132V127Q${x - 43} 108 ${x - 23} 108H${x + 23}Q${x + 43} 108 ${x + 43} 127V132`} />)}
          <path d="M919 123H932V139H923M910 124V146H924" />
        </g>
        {fifthWheel != null && <g data-profile-fifth-wheel><path d={`M${fifthWheel - 30} 105H${fifthWheel + 30}M${fifthWheel} 104V116`} stroke="#0284c7" strokeWidth="3" /></g>}
      </>}
      {kind === 'van' && <g data-profile-van>
        <path d={`M80 117V85L114 76L${cab - 15} 20Q${cab} 12 ${cab + 30} 12H883Q920 12 920 40V120H80Z`} className="fill-slate-50 dark:fill-slate-900" />
        <path d={`M125 78L${cab - 6} 27H${cab + 24}V80H125Z`} className="fill-sky-100 dark:fill-slate-700" />
        <path d={`M${cab + 37} 20V113M${cab + 51} 79H866V22M912 35V110M${cab + 25} 89h-16M80 116H920`} />
      </g>}
      {kind === 'custom' && <>{cargo(left)}<path d="M80 124H920" /></>}
    </g>
  </svg>
}
